import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Modal } from "components/designV2/modal/modal";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import {
  DatePickerSlot,
  DuplicateDatesDescription,
  DuplicateDatesLead,
  DuplicateDatesList,
  DuplicateDatesModalActions,
  DuplicateDatesModalContent,
  EmptyContent,
  EmptyDescription,
  EmptyState,
  EmptyTitle,
  ErrorText,
  LoadingSpinner,
  PeriodBar,
  TipsContainer,
  TipsTabActions,
  TipsTabBar,
} from "./tips.styled";
import { useTipsViewModel } from "./tips.component.logic";
import { TipsDistributionModal } from "./modals/distribution/tips.distributionModal.component";
import { TipsMonthlyReports } from "./monthlyReports/tips.monthlyReports.component";
import { TipsRows } from "./rows/tips.rows.component";
import { TipsSummary } from "./summary/tips.summary.component";
import { TipsTransactions } from "./transactions/tips.transactions.component";

const formatDuplicateDate = (value: string): string => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}`;
};

export const Tips = (): JSX.Element => {
  const viewModel = useTipsViewModel();
  const duplicateDatesText = viewModel.duplicateDateKeys
    .map(formatDuplicateDate)
    .join(", ");

  const addDatesButton = (
    <DatePicker
      allowRangePicker
      allowMultiSelectInSingleMode={false}
      companyId={viewModel.companyId}
      dateFormat="DD/MM/YYYY"
      menuAlignment="right"
      onRangeChange={(dates) => {
        void viewModel.handleSelectedDatesChange(dates);
      }}
      showExcludeToggles={false}
      size="compact"
      trigger="button"
      triggerIconName="v2/plus"
      triggerLabel={viewModel.labels.addDates}
      value={viewModel.selectedDates[0] ?? null}
    />
  );

  return (
    <TipsContainer>
      <TipsTabBar>
        <SegmentController
          options={[...viewModel.tabOptions]}
          value={viewModel.activeTab}
          onChange={viewModel.setActiveTab}
        />
        {viewModel.activeTab === "tips" && viewModel.hasTips && (
          <TipsTabActions>{addDatesButton}</TipsTabActions>
        )}
      </TipsTabBar>

      {viewModel.activeTab !== "transactions" && (
        <PeriodBar>
          <PeriodNavigator
            currentDate={viewModel.currentDate}
            mode="month"
            onChange={viewModel.setCurrentDate}
            prevAriaLabel={viewModel.labels.previousMonth}
            nextAriaLabel={viewModel.labels.nextMonth}
          />
          {viewModel.activeTab === "tips" && viewModel.hasTips && (
            <TipsSummary
              activeFilter={viewModel.activeSummaryFilter}
              summary={viewModel.summary}
              onFilterChange={viewModel.setActiveSummaryFilter}
            />
          )}
        </PeriodBar>
      )}

      {viewModel.errorMessage && (
        <ErrorText>{viewModel.errorMessage}</ErrorText>
      )}

      {viewModel.activeTab === "transactions" ? (
        <TipsTransactions
          companyId={viewModel.companyId}
          currentDate={viewModel.currentDate}
          onCurrentDateChange={viewModel.setCurrentDate}
        />
      ) : viewModel.isLoadingTips && !viewModel.hasTips ? (
        <EmptyState>
          <LoadingSpinner role="status" aria-label={viewModel.labels.loading} />
        </EmptyState>
      ) : viewModel.activeTab === "monthlyReports" ? (
        <TipsMonthlyReports
          currentDate={viewModel.currentDate}
          tips={viewModel.tips}
        />
      ) : viewModel.activeTab === "tips" && viewModel.hasTips ? (
        <TipsRows
          tips={viewModel.filteredTips}
          onDelete={(companyTipId) => {
            void viewModel.handleDeleteTip(companyTipId);
          }}
          onDeleteMany={(companyTipIds) => {
            void viewModel.handleDeleteTips(companyTipIds);
          }}
          onDistribute={(companyTipId, amount) => {
            void viewModel.handleOpenDistributionModal(companyTipId, amount);
          }}
          onDistributeMany={(companyTipIds) => {
            void viewModel.handleOpenBulkDistributionModal(companyTipIds);
          }}
          onPayout={viewModel.handlePayoutTips}
          onRevertManualPayout={(companyTipIds) => {
            void viewModel.handleRevertManualPayout(companyTipIds);
          }}
          onUpdateAmount={(companyTipId, amount) => {
            void viewModel.handleUpdateTipAmount(companyTipId, amount);
          }}
        />
      ) : (
        <EmptyState>
          <EmptyContent>
            <EmptyTitle>{viewModel.emptyStateCopy.title}</EmptyTitle>
            <EmptyDescription>
              {viewModel.emptyStateCopy.description}
            </EmptyDescription>
            {viewModel.activeTab === "tips" && (
              <DatePickerSlot>{addDatesButton}</DatePickerSlot>
            )}
          </EmptyContent>
        </EmptyState>
      )}
      <TipsDistributionModal
        calculationResult={viewModel.distributionResult}
        isOpen={
          viewModel.distributionTip !== null &&
          viewModel.distributionResult !== null
        }
        isSaving={viewModel.isSavingDistribution}
        tip={viewModel.distributionTip}
        onClose={viewModel.closeDistributionModal}
        onRecalculateTeamDistribution={
          viewModel.handleRecalculateTeamDistribution
        }
        onSaveHourDistribution={viewModel.handleSaveHourDistribution}
        onSaveTeamDistribution={viewModel.handleSaveTeamDistribution}
        onUndoDistribution={viewModel.handleUndoDistribution}
      />
      <Modal
        isOpen={viewModel.duplicateDateKeys.length > 0}
        onClose={viewModel.closeDuplicateDatesModal}
        label={viewModel.labels.addDates}
        size="compact"
        footer={
          <DuplicateDatesModalActions>
            <SecondaryButton
              label={viewModel.labels.addAnyway}
              onClick={() => {
                void viewModel.confirmAddDuplicateDates();
              }}
            />
            <PrimaryButton
              label={viewModel.labels.onlyNewDates}
              onClick={viewModel.closeDuplicateDatesModal}
            />
          </DuplicateDatesModalActions>
        }
      >
        <DuplicateDatesModalContent>
          <DuplicateDatesLead>
            {viewModel.labels.followingDatesAlreadyExist}
          </DuplicateDatesLead>
          <DuplicateDatesList>{duplicateDatesText}</DuplicateDatesList>
          <DuplicateDatesDescription>
            {viewModel.labels.addAnywaysOrOnlyNewDates}
          </DuplicateDatesDescription>
        </DuplicateDatesModalContent>
      </Modal>
    </TipsContainer>
  );
};
